import { useEffect, useMemo, useState } from "react";

import { inject } from "../../../../../lib/itn-ui-kit/services/service-container";
import { useFetchFilterOpts } from "../../CaseList/hooks/useFetchFilterOpts";
import { createActiveChipLabel } from "../../CaseList/utils/functions";
import { MultiSelectDB, SingleSelectDB } from "../../CaseList/utils/store";

export const useDrawerFiltersState = ({ filterConfig = [], filters }) => {
  const currentUserService = inject("current-user-service");
  const { getOptions } = useFetchFilterOpts();
  const [filterState, setFilterState] = useState([]);

  const allowedFilters = useMemo(() => {
    return filterConfig.filter((filter) => {
      if (filter?.permission?.length > 0) {
        return currentUserService?.userRoles?.permissions.includes(filter.permission);
      }
      return true;
    });
  }, [filterConfig, currentUserService?.userRoles?.permissions]);

  const activeFilters = useMemo(
    () => filterState.filter((filter) => filter.activeChip.isActive),
    [filterState]
  );

  async function initializeFilterState() {
    const updatedFilterState = [];

    for (const filter of allowedFilters) {
      const updatedFilter = {
        ...filter,
        currentValue: null,
        activeChip: {
          ...filter.activeChip,
          isActive: false,
          label: "",
        },
      };

      switch (filter.filterType) {
        case "MultiSelectSearch": {
          const filterValue =
            filters.get(filter.fieldName) || filters.get(filter.fieldName + "__in");

          if (filterValue) {
            let existingValues = null;
            const parsedValue = filterValue
              .split(",")
              .map((v) => v.trim())
              .filter((v) => v.length > 0);
            updatedFilter.currentValue = parsedValue;

            try {
              const storedOpts = await MultiSelectDB.get(filter.fieldName);
              const hasStaticOptions = Array.isArray(filter.options) && filter.options.length > 0;

              if (hasStaticOptions) {
                const valuesToStore = filter.options.filter((opt) =>
                  parsedValue.includes(String(opt[filter.valueKey]))
                );
                existingValues = valuesToStore.map((opt) => opt[filter.nameKey]);
                await MultiSelectDB.save(filter.fieldName, valuesToStore);
              } else if (storedOpts?.length < parsedValue.length || !storedOpts) {
                const opts = await getOptions({
                  apiUrl: filter.apiUrl,
                  valueKey: filter.valueKey,
                  selectedItems: parsedValue,
                  limit: 1000,
                });

                if (opts && opts.length > 0) {
                  const valuesToStore = opts.filter((opt) =>
                    parsedValue.includes(String(opt[filter.valueKey]))
                  );
                  existingValues = valuesToStore.map((opt) => opt[filter.nameKey]);

                  await MultiSelectDB.save(filter.fieldName, valuesToStore);
                }
              } else {
                existingValues = storedOpts.map((opt) => opt[filter.nameKey]);
              }
            } catch (error) {
              console.error(`Error loading initial options for ${filter.fieldName}:`, error);
            }

            updatedFilter.activeChip = {
              ...filter.activeChip,
              isActive: parsedValue.length > 0,
              label: createActiveChipLabel(existingValues ?? parsedValue, filter.filterType),
            };
          }
          break;
        }

        case "SingleSelect": {
          const filterValue = filters.get(filter.fieldName);

          if (filterValue) {
            let existingValues = null;
            updatedFilter.currentValue = filterValue;

            try {
              const storedOpt = await SingleSelectDB.get(filter.fieldName);
              const hasStaticOptions = Array.isArray(filter.options) && filter.options.length > 0;

              if (hasStaticOptions) {
                const found = filter.options.find(
                  (opt) => String(opt[filter.valueKey]) === String(filterValue)
                );
                if (found) {
                  existingValues = [found[filter.nameKey]];
                  await SingleSelectDB.save(filter.fieldName, found);
                }
              } else if (storedOpt) {
                existingValues = [storedOpt[filter.nameKey]];
              } else {
                const opts = await getOptions({
                  apiUrl: filter.apiUrl,
                  valueKey: filter.valueKey,
                  selectedItems: [filterValue],
                });

                if (opts && opts.length > 0) {
                  const foundInNew = opts.find(
                    (opt) => String(opt[filter.valueKey]) === String(filterValue)
                  );
                  if (foundInNew) {
                    existingValues = [foundInNew[filter.nameKey]];
                    await SingleSelectDB.save(filter.fieldName, foundInNew);
                  }
                }
              }
            } catch (error) {
              console.error(`Error loading initial options for ${filter.fieldName}:`, error);
            }

            updatedFilter.activeChip = {
              ...filter.activeChip,
              isActive: true,
              label: createActiveChipLabel(existingValues?.[0] ?? filterValue, filter.filterType),
            };
          }
          break;
        }

        case "DateRange": {
          const gteValue = filters.get(filter.fieldName + "__gte");
          const lteValue = filters.get(filter.fieldName + "__lte");

          if (gteValue || lteValue) {
            updatedFilter.currentValue = {
              __gte: gteValue || undefined,
              __lte: lteValue || undefined,
            };
            updatedFilter.activeChip = {
              ...filter.activeChip,
              isActive: !!(gteValue || lteValue),
              label: createActiveChipLabel({ __gte: gteValue, __lte: lteValue }, filter.filterType),
            };
          }
          break;
        }

        case "SearchOptionGroup": {
          const filterValue = filters.get(filter.fieldName);

          if (filterValue) {
            updatedFilter.currentValue = filterValue;
            updatedFilter.activeChip = {
              ...filter.activeChip,
              isActive: true,
              label: createActiveChipLabel(
                filterValue,
                filter.filterType,
                filter.customTrueLabel ?? "Yes",
                filter.customFalseLabel ?? "No"
              ),
            };
          }
          break;
        }

        case "DatePicker": {
          const filterValue = filters.get(filter.fieldName);

          if (filterValue) {
            updatedFilter.currentValue = filterValue;
            updatedFilter.activeChip = {
              ...filter.activeChip,
              isActive: true,
              label: createActiveChipLabel(filterValue, filter.filterType),
            };
          }
          break;
        }

        case "RangeSlider": {
          const gteValue = filters.get(filter.fieldName + "__gte");
          const lteValue = filters.get(filter.fieldName + "__lte");

          if (gteValue || lteValue) {
            updatedFilter.currentValue = {
              __gte: gteValue || undefined,
              __lte: lteValue || undefined,
            };
            updatedFilter.activeChip = {
              ...filter.activeChip,
              isActive: !!(gteValue || lteValue),
              label: createActiveChipLabel({ __gte: gteValue, __lte: lteValue }, filter.filterType),
            };
          }
          break;
        }

        default: {
          const filterValue = filters.get(filter.fieldName);

          if (filterValue) {
            let parsedValue;
            try {
              parsedValue = JSON.parse(filterValue);
            } catch (e) {
              parsedValue = filterValue;
            }
            updatedFilter.currentValue = parsedValue;
            updatedFilter.activeChip = {
              ...filter.activeChip,
              isActive: true,
              label: createActiveChipLabel(parsedValue, filter.filterType),
            };
          }
          break;
        }
      }

      updatedFilterState.push(updatedFilter);
    }

    setFilterState(updatedFilterState);
  }

  useEffect(() => {
    if (!filters) {
      setFilterState([]);
      return;
    }
    initializeFilterState();
  }, [allowedFilters, filters]);

  return {
    activeFilters,
    filterState,
  };
};
